import React, { useState, useEffect } from 'react';
import Calendar from 'react-calendar';
import { Form, Container, Row, Col, Card } from 'react-bootstrap';
import 'react-calendar/dist/Calendar.css';
import { ActividadIndex } from '../../../interfaces/ActividadIndexInterface';

// Estilos personalizados
const calendarStyles = {
  container: {
    marginBottom: '20px',
  },
  calendar: {
    width: '100%',
    border: '1px solid #ddd',
    borderRadius: '8px',
    padding: '10px',
    boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
  },
  eventList: {
    marginTop: '20px',
  },
  eventCard: {
    marginBottom: '10px',
    borderLeft: '4px solid #5DADE2',
  },
  filterContainer: {
    marginBottom: '20px',
  },
};

interface ServiciosCalendarProps {
  actividades: ActividadIndex[];
}

const ServiciosCalendar: React.FC<ServiciosCalendarProps> = ({ actividades }) => {
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [selectedTecnico, setSelectedTecnico] = useState<string>('todos');
  const [tecnicos, setTecnicos] = useState<{ id: string; nombre: string }[]>([]);
  const [serviciosFiltrados, setServiciosFiltrados] = useState<ActividadIndex[]>([]);

  // Extraer la lista única de técnicos de las actividades
  useEffect(() => {
    if (actividades && actividades.length > 0) {
      const tecnicosUnicos = actividades.reduce((acc: { id: string; nombre: string }[], actividad) => {
        const tecnicoId = actividad.usuario.id.toString();
        const tecnicoNombre = `${actividad.usuario.nombre} ${actividad.usuario.apellido_paterno} ${actividad.usuario.apellido_materno || ''}`.trim();
        
        if (!acc.some(t => t.id === tecnicoId)) {
          acc.push({ id: tecnicoId, nombre: tecnicoNombre });
        }
        
        return acc;
      }, []);
      
      setTecnicos(tecnicosUnicos);
    }
  }, [actividades]);

  // Filtrar servicios por fecha y técnico seleccionados
  useEffect(() => {
    if (actividades && actividades.length > 0) {
      let filtrados = [...actividades];
      
      // Filtrar por fecha
      if (selectedDate) {
        filtrados = filtrados.filter(servicio => {
          if (!servicio.fecha) return false;
          
          const servicioDate = new Date(servicio.fecha);
          return (
            servicioDate.getDate() === selectedDate.getDate() &&
            servicioDate.getMonth() === selectedDate.getMonth() &&
            servicioDate.getFullYear() === selectedDate.getFullYear()
          );
        });
      }
      
      // Filtrar por técnico
      if (selectedTecnico !== 'todos') {
        filtrados = filtrados.filter(servicio => 
          servicio.usuario.id.toString() === selectedTecnico
        );
      }
      
      setServiciosFiltrados(filtrados);
    }
  }, [actividades, selectedDate, selectedTecnico]);

  // Función para marcar fechas con servicios en el calendario
  const tileContent = ({ date, view }: { date: Date; view: string }) => {
    if (view !== 'month') return null;
    
    // Filtrar actividades para esta fecha
    const serviciosEnFecha = actividades.filter(servicio => {
      if (!servicio.fecha) return false;
      
      const servicioDate = new Date(servicio.fecha);
      return (
        servicioDate.getDate() === date.getDate() &&
        servicioDate.getMonth() === date.getMonth() &&
        servicioDate.getFullYear() === date.getFullYear() &&
        (selectedTecnico === 'todos' || servicio.usuario.id.toString() === selectedTecnico)
      );
    });
    
    if (serviciosEnFecha.length > 0) {
      return (
        <div className="position-absolute bottom-0 start-50 translate-middle-x" style={{ fontSize: '10px' }}>
          <span className="badge bg-primary">{serviciosEnFecha.length}</span>
        </div>
      );
    }
    
    return null;
  };

  return (
    <Container style={calendarStyles.container}>
      <Row style={calendarStyles.filterContainer}>
        <Col md={6}>
          <Form.Group>
            <Form.Label>Filtrar por Técnico</Form.Label>
            <Form.Select 
              value={selectedTecnico} 
              onChange={(e) => setSelectedTecnico(e.target.value)}
            >
              <option value="todos">Todos los técnicos</option>
              {tecnicos.map(tecnico => (
                <option key={tecnico.id} value={tecnico.id}>
                  {tecnico.nombre}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
      </Row>
      
      <Row>
        <Col>
          <div style={calendarStyles.calendar}>
            <Calendar 
              onChange={(value) => {
                if (value instanceof Date) {
                  setSelectedDate(value);
                } else if (Array.isArray(value) && value[0] instanceof Date) {
                  setSelectedDate(value[0]);
                }
              }} 
              value={selectedDate}
              tileContent={tileContent}
              locale="es-ES"
            />
          </div>
        </Col>
      </Row>
      
      <Row style={calendarStyles.eventList}>
        <Col>
          <h4>Servicios para {selectedDate.toLocaleDateString('es-ES', { 
            weekday: 'long', 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric' 
          })}</h4>
          
          {serviciosFiltrados.length === 0 ? (
            <p>No hay servicios programados para esta fecha{selectedTecnico !== 'todos' ? ' y técnico seleccionado' : ''}.</p>
          ) : (
            serviciosFiltrados.map(servicio => (
              <Card key={servicio.id} style={calendarStyles.eventCard}>
                <Card.Body>
                  <Card.Title>Servicio #{servicio.id}</Card.Title>
                  <Card.Subtitle className="mb-2 text-muted">
                    {servicio.fecha ? new Date(servicio.fecha).toLocaleTimeString('es-ES', {
                      hour: '2-digit',
                      minute: '2-digit'
                    }) : 'Sin hora asignada'}
                  </Card.Subtitle>
                  <Card.Text>
                    <strong>Técnico:</strong> {`${servicio.usuario.nombre} ${servicio.usuario.apellido_paterno}`}<br />
                    <strong>Tipo de servicio:</strong> {servicio.tipo_servicio.nombre}<br />
                    <strong>Folio:</strong> {servicio.servicio.folio || 'No especificado'}<br />
                    <strong>VIN:</strong> {servicio.unidad.vin || 'No especificado'}<br />
                    <strong>Placas:</strong> {servicio.unidad.placa || 'No especificado'}
                  </Card.Text>
                </Card.Body>
              </Card>
            ))
          )}
        </Col>
      </Row>
    </Container>
  );
};

export default ServiciosCalendar;
